import { useMemo, useContext } from "react"
import { isEmpty } from "lodash"
import { useLastIntradayBatch } from "hooks/intraday"
import { DataStatus, OK } from "helpers/data-statuses"
import { getToday, IsoDate } from "helpers/types/dates"
import { IntradayBatchMetadata } from "model/state"
import { DashboardContext } from "../context/dashboard"

/**
 * Helper function to calculate intraday state
 * @param isIntradayEnabled - Base intraday flag
 * @param endDate - Current end date
 * @param intradayDate - Today's date (intraday date)
 * @param lastIntradayBatch - Intraday batch data
 * @param intradayBatchStatus - Status of intraday batch
 */
export function calculateIntradayState(
  isIntradayEnabled: boolean,
  endDate: IsoDate,
  intradayDate: IsoDate,
  lastIntradayBatch: IntradayBatchMetadata | null,
  intradayBatchStatus: DataStatus,
): [{
  isIntradayMode: boolean
  isIntradayBatchAvailable: boolean
  isEndDateToday: boolean
}, DataStatus] {
  const isIntradayBatchAvailable = !isEmpty(lastIntradayBatch) && intradayBatchStatus === OK
  const isEndDateToday = endDate === intradayDate
  const isIntradayMode = isIntradayEnabled && isEndDateToday

  return [
    {
      isIntradayMode,
      isIntradayBatchAvailable,
      isEndDateToday,
    },
    intradayBatchStatus,
  ]
}

/**
 * Hook to get intraday state using context values
 */
export function useIntradayState() {
  const { isIntradayEnabled, endDate, instrument, instrumentsMetadata } = useContext(DashboardContext)
  const [lastIntradayBatch, intradayBatchStatus] = useLastIntradayBatch(instrument, instrumentsMetadata?.original?.intradayEnabled)
  const today = getToday()

  return useMemo(() => {
    return calculateIntradayState(isIntradayEnabled, endDate, today, lastIntradayBatch, intradayBatchStatus)
  }, [isIntradayEnabled, endDate, today, lastIntradayBatch, intradayBatchStatus])
}
